import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/button';
import FileUpload from '../../components/FileUpload';
import Input from '../../components/input';
import Select from '../../components/Select';
import { clearVehicleDetail, fetchServiceTagsDropdown, fetchVehicleDetail, fetchVehicleDropdown, getSingleService, updateServiceEntry } from '../../store/Slices/ServiceSlice';
import '../../styles/ServiceEntry.css';
import { maintenanceTypesDropdown, repairPriorityDropdown, serviceStatusOptions } from '../../utils/Dropdowns';
import { hasPermission, slugfy } from '../../utils/helper';
import { serviceEntryValidationSchema } from '../../utils/Schema';
import { errorNotify, successNotify } from '../../utils/toast';
import IssueTracking from './IssueTracking';
import ServiceLineItem from './ServiceLineItem';

const EditServiceEntry = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [initialLoaded, setInitialLoaded] = useState(false);
    const [link_issue_ids, setLink_issue_ids] = useState([]);
    const [updateIssue, setUpdateIssue] = useState(false);
    const [checkedIssueIds, setCheckedIssueIds] = useState(() => {
        // Load from localStorage on mount
        const saved = localStorage.getItem(`checkedIssueIds_edit_${id}`);
        return saved ? JSON.parse(saved) : [];
    });

    const { loading, vehicleData, vehicleDetail, vehicleDetailLoading,
        singleServiceData, singleServiceLoading, serviceTagsData,
        serviceTagsLoading, } = useSelector((state) => state.services);

    const [discountPercent, setDiscountPercent] = useState(singleServiceData?.discount || 0);
    const [taxPercent, setTaxPercent] = useState(singleServiceData?.cost_tax || 0);

    const canUpdate = hasPermission('services', 'update');

    useEffect(() => {
        dispatch(getSingleService(id));
        dispatch(fetchVehicleDropdown())
        dispatch(fetchServiceTagsDropdown())

        return () => {
            dispatch(clearVehicleDetail());
            // Clear localStorage on unmount
            const storageKey = `checkedIssueIds_edit_${id}`;
            localStorage.removeItem(storageKey);
        }
    }, [id]);

    useEffect(() => {
        if (singleServiceData?.vehicle) {
            setSelectedVehicle({
                value: singleServiceData.vehicle.id,
                label: singleServiceData.vehicle.registration_number,
                brand_id: singleServiceData.vehicle.brand_id,
                ref_id: singleServiceData.id
            });
            dispatch(fetchVehicleDetail({
                vehicleId: singleServiceData.vehicle.id,
                vehicleBrandId: singleServiceData.vehicle.brand_id,
                ref_id: singleServiceData.id
            }));
            setInitialLoaded(true);
        }

        if (singleServiceData?.images) {
            setUploadedFiles(singleServiceData?.images.map(img => ({
                fileName: img.file_path,
                originalName: img.file_path,
                previewUrl: null,
                backend: true
            })));
        }

        setDiscountPercent(parseInt(singleServiceData?.discount) || 0);
        setTaxPercent(parseInt(singleServiceData?.cost_tax) || 0);

    }, [singleServiceData]);

    // Save to localStorage whenever checkedIssueIds changes
    useEffect(() => {
        const storageKey = `checkedIssueIds_edit_${id}`;
        localStorage.setItem(storageKey, JSON.stringify(checkedIssueIds));
    }, [checkedIssueIds, id]);

    const tagsOptions = serviceTagsData?.map(tag => ({ label: tag.tag_name, value: tag.tag_name }));

    const calculateCostSummary = () => {
        if (!vehicleDetail?.vehicle_service_line_items) {
            return {
                cost_parts: singleServiceData?.cost_parts || 0,
                cost_labor: singleServiceData?.cost_labor || 0,
                sub_total: singleServiceData?.sub_total || 0,
                discount: singleServiceData?.discount || 0,
                tax: singleServiceData?.cost_tax || 0,
            };
        }

        const items = vehicleDetail.vehicle_service_line_items;
        const cost_parts = items.reduce((sum, item) => sum + parseFloat(item.cost_parts || 0), 0);
        const cost_labor = items.reduce((sum, item) => sum + parseFloat(item.cost_labor || 0), 0);
        const sub_total = cost_parts + cost_labor;

        return {
            cost_parts: cost_parts.toFixed(2),
            cost_labor: cost_labor.toFixed(2),
            sub_total: sub_total.toFixed(2)
        };
    };

    const costSummary = calculateCostSummary();

    // Prepare tags array
    const tagsArr = singleServiceData?.tags
        ? Array.isArray(singleServiceData.tags)
            ? singleServiceData.tags
            : [{ value: singleServiceData.tags.tag_name, label: singleServiceData.tags.tag_name }]
        : [];

    const getRefNumber = `SE-${slugfy(vehicleDetail?.registration_number)}-${parseInt(id)}`;

    // Initial values for Formik
    const initialValues = {
        vehicle_id: singleServiceData?.vehicle_id || '',
        odm: singleServiceData?.odo_meter || 0,
        odo_meter: singleServiceData?.odo_meter || 0,
        status: singleServiceData?.status || '',
        ref_number: vehicleDetail?.registration_number ? getRefNumber.toUpperCase() || '' : '',
        priority: singleServiceData?.priority || '',
        services_type: singleServiceData?.services_type || '',
        start_at: singleServiceData?.start_at ? singleServiceData.start_at.slice(0, 10) : '',
        complete_at: singleServiceData?.complete_at ? singleServiceData.complete_at.slice(0, 16) : '',
        note: singleServiceData?.note || '',
        tags: tagsArr
    };

    const handleVehicleChange = (selectedOption, setFieldValue) => {
        setSelectedVehicle(selectedOption);
        setFieldValue('vehicle_id', selectedOption?.value || '');
        if (selectedOption) {
            dispatch(fetchVehicleDetail({
                vehicleId: selectedOption.value,
                vehicleBrandId: selectedOption.brand_id,
                ref_id: selectedOption.ref_id
            }));
        }
    };

    const handleDiscountChange = (e) => {
        let val = e.target.value.replace(/[^0-9.]/g, '');
        val = val === '' ? '' : Math.max(0, Math.min(100, Number(val)));
        setDiscountPercent(val);
    };
    const handleTaxChange = (e) => {
        let val = e.target.value.replace(/[^0-9.]/g, '');
        val = val === '' ? '' : Math.max(0, Math.min(100, Number(val)));
        setTaxPercent(val);
    };

    const calculateTotal = () => {
        const subTotal = parseFloat(costSummary.sub_total);
        const discount = parseFloat(discountPercent) || 0;
        const tax = parseFloat(taxPercent) || 0;
        const discountAmount = subTotal * (discount / 100);
        const taxAmount = subTotal * (tax / 100);
        return (subTotal + taxAmount - discountAmount).toFixed(2);
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            if ((vehicleDetail?.vehicle_service_line_items || []).length === 0) {
                errorNotify("At least one Service Line Item is required!");
                return;
            }

            // Validate discount/tax percent
            if (discountPercent < 0 || discountPercent > 100) {
                errorNotify("Discount must be between 0 and 100%");
                return;
            }
            if (taxPercent < 0 || taxPercent > 100) {
                errorNotify("Tax must be between 0 and 100%");
                return;
            }

            const subTotal = parseFloat(costSummary.sub_total);
            const discount = parseFloat(discountPercent) || 0;
            const tax = parseFloat(taxPercent) || 0;
            const discountAmount = subTotal * (discount / 100);
            const taxAmount = subTotal * (tax / 100);
            const complete_total = subTotal + taxAmount - discountAmount;

            const serviceData = {
                maintenances_service_id: singleServiceData?.id,
                vehicle_id: parseInt(values.vehicle_id),
                odo_meter: parseInt(values.odm),
                priority: values.priority,
                ref_number: values.ref_number,
                services_type: values.services_type,
                status: values.status,
                start_at: values.start_at ? new Date(values.start_at).toISOString() : null,
                complete_at: values.complete_at ? new Date(values.complete_at).toISOString() : null,
                note: values.note,
                cost_parts: parseFloat(costSummary.cost_parts),
                cost_labor: parseFloat(costSummary.cost_labor),
                cost_tax: taxPercent,
                discount: discountPercent,
                sub_total: subTotal,
                complete_total: complete_total,
                tags: values.tags.map(tag => tag.value || tag),
                files: uploadedFiles.map(file => file.fileName),
                link_issue_ids: link_issue_ids,
                line_item: (vehicleDetail?.vehicle_service_line_items || []).map(item => ({
                    line_item_id: item.id,
                    service_issue_id: item.service_issue_id
                })),
                updatedKeys: updateIssue ? ['link_issue_ids'] : []
            };

            await dispatch(updateServiceEntry(serviceData)).unwrap();
            successNotify('Service entry updated successfully');
            
            // Clear localStorage after successful save
            const storageKey = `checkedIssueIds_edit_${id}`;
            localStorage.removeItem(storageKey);
            
            resetForm();
            setSelectedVehicle(null);
            setUploadedFiles([]);
            clearVehicleDetail();
            navigate('/services');
        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    const refreshVehicleDetail = async (source = 'both') => {
        if (selectedVehicle) {
            const response = await dispatch(fetchVehicleDetail({
                vehicleId: selectedVehicle.value,
                vehicleBrandId: selectedVehicle.brand_id,
                ref_id: selectedVehicle.ref_id,
            }));
            return { response, source };
        }
    };

    if (singleServiceLoading) {
        return <div className='loading_spinner'><Spinner animation="grow" /></div>;
    }

    return (
        <>
            <Breadcrumbs values={['Maintenance', 'Overview', 'Edit Service Entry']} />

            <Formik
                initialValues={initialValues}
                validationSchema={serviceEntryValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
                    <Form>
                        <div className='access_view'>
                            <h2>Edit Service Entry</h2>
                            <div className='service_entry_btn'>
                                <Button
                                    cancelBtn={() => navigate('/services')}
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                    submitText="Update Service Entry"
                                    type="submit"
                                    shouldRow={false}
                                    isShowSubmitBtn={canUpdate}
                                />
                            </div>
                        </div>

                        <div className='service_entry_main'>
                            <div className='container-fluid'>
                                <div className='row'>
                                    <div className='col-md-8'>
                                        <div className='service_entry_details'>
                                            <h6>Service Entry Details</h6>
                                            <p>Edit information for this service record.</p>

                                            <div className='vehicle_details_fields'>
                                                <h5>Vehicle Details</h5>
                                                <Row>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Vehicle"
                                                            placeholder="Select Vehicle"
                                                            options={vehicleData}
                                                            loading={loading}
                                                            name="vehicle_id"
                                                            value={selectedVehicle}
                                                            onChange={(selectedOption) => handleVehicleChange(selectedOption, setFieldValue)}
                                                            onBlur={handleBlur}
                                                            error={errors.vehicle_id}
                                                            touched={touched.vehicle_id}
                                                            isDisabled={true}
                                                            isSearch={false}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Repair Priority"
                                                            placeholder="Select Repair Priority"
                                                            options={repairPriorityDropdown}
                                                            loading={false}
                                                            name="priority"
                                                            value={repairPriorityDropdown.find(option => option.value === values.priority)}
                                                            onChange={(selectedOption) => setFieldValue('priority', selectedOption?.value || '')}
                                                            onBlur={handleBlur}
                                                            error={errors.priority}
                                                            touched={touched.priority}
                                                            isSearch={false}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Input
                                                            label="Odometer Reading"
                                                            placeholder="Enter Odometer Reading"
                                                            type="number"
                                                            name="odm"
                                                            value={values.odm}
                                                            showExtraText={vehicleDetail ? true : false}
                                                            extraText={`Last Recorded: ${vehicleDetail?.last_service?.odo_meter || 0
                                                                } KM`}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={errors.odm}
                                                            touched={touched.odm}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Maintenance Type"
                                                            placeholder="Select Maintenance Type"
                                                            options={maintenanceTypesDropdown}
                                                            loading={false}
                                                            name="services_type"
                                                            value={maintenanceTypesDropdown.find(option => option.value === values.services_type)}
                                                            onChange={(selectedOption) => setFieldValue('services_type', selectedOption?.value || '')}
                                                            onBlur={handleBlur}
                                                            error={errors.services_type}
                                                            touched={touched.services_type}
                                                            isSearch={false}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Input
                                                            label="Reference Number"
                                                            placeholder="Enter Reference"
                                                            type="text"
                                                            name="ref_number"
                                                            value={values.ref_number}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={errors.ref_number}
                                                            touched={touched.ref_number}
                                                            disabled
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Status"
                                                            placeholder="Select Status"
                                                            options={serviceStatusOptions}
                                                            loading={false}
                                                            name="status"
                                                            value={serviceStatusOptions.find(option => option.value === values.status)}
                                                            onChange={(selectedOption) => setFieldValue('status', selectedOption?.value || '')}
                                                            onBlur={handleBlur}
                                                            error={errors.status}
                                                            touched={touched.status}
                                                            isSearch={false}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>

                                            <div className='vehicle_details_fields'>
                                                <h5>Repair Details</h5>
                                                <Row>
                                                    <Col md={6}>
                                                        <Input
                                                            label="Completion Date & Time"
                                                            placeholder=""
                                                            type="datetime-local"
                                                            name="complete_at"
                                                            value={values.complete_at}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={errors.complete_at}
                                                            touched={touched.complete_at}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Input
                                                            label="Start Date (Optional)"
                                                            placeholder=""
                                                            type="date"
                                                            name="start_at"
                                                            value={values.start_at}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={errors.start_at}
                                                            touched={touched.start_at}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Tags"
                                                            placeholder="Type and press Enter to create tags"
                                                            options={tagsOptions}
                                                            loading={serviceTagsLoading}
                                                            name="tags"
                                                            value={values.tags}
                                                            onChange={(selectedOptions) => setFieldValue('tags', selectedOptions || [])}
                                                            onBlur={handleBlur}
                                                            isMulti={true}
                                                            isCreatable={true}
                                                            error={errors.tags && typeof errors.tags === 'string' ? errors.tags : null}
                                                            touched={touched.tags}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>

                                        <IssueTracking vehicleDetailLoading={vehicleDetailLoading} vehicleDetail={vehicleDetail}
                                            selectedVehicle={selectedVehicle}
                                            setLink_issue_ids={setLink_issue_ids}
                                            link_issue_ids={link_issue_ids}
                                            setUpdateIssue={setUpdateIssue}
                                            refreshVehicleDetail={() => refreshVehicleDetail('issue')}
                                            checkedIssueIds={checkedIssueIds}
                                            setCheckedIssueIds={setCheckedIssueIds}
                                        />
                                        <ServiceLineItem
                                            vehicleDetailLoading={vehicleDetailLoading} 
                                            vehicleDetail={vehicleDetail ? vehicleDetail : singleServiceData}
                                            selectedVehicle={selectedVehicle}
                                            refreshVehicleDetail={() => refreshVehicleDetail('service')}
                                        />

                                        <div className='photo_document_section'>
                                            <h6>Photos & Documents</h6>
                                            <p>Upload relevant photos or documents for this service.</p>
                                            <FileUpload
                                                destination="service-entry"
                                                files={uploadedFiles}
                                                setFiles={setUploadedFiles}
                                                maxFileSize={10}
                                            />
                                        </div>

                                        <div className='my-4'>
                                            <Button
                                                cancelBtn={() => navigate('/services')}
                                                loading={isSubmitting}
                                                disabled={isSubmitting}
                                                submitText="Update Service Entry"
                                                type="submit"
                                            />
                                        </div>
                                    </div>
                                    <div className='col-md-4'>
                                        <div className='service_cost_summary_details'>
                                            <div className='service_cost_summary'>
                                                <h6>Cost Summary</h6>
                                                <p>Financial breakdown of the service entries.</p>
                                                <div className='subtotal'>
                                                    <div>Subtotal (Calculated)</div>
                                                    <span>PKR {costSummary.sub_total}</span>
                                                </div>
                                                <div className='service_item'>
                                                    <div>Parts</div>
                                                    <div>PKR {costSummary.cost_parts}</div>
                                                </div>
                                                <div className='service_item'>
                                                    <div>Labor</div>
                                                    <div>PKR {costSummary.cost_labor}</div>
                                                </div>
                                                <div className="service_item">
                                                    <div>Discount (%)</div>
                                                    <div>
                                                        <input
                                                            className='edit_input'
                                                            type="number"
                                                            min={0}
                                                            max={100}
                                                            value={discountPercent}
                                                            onChange={handleDiscountChange}
                                                            placeholder='0'
                                                        /> %
                                                    </div>
                                                </div>
                                                <div className="service_item">
                                                    <div>Tax (%)</div>
                                                    <div>
                                                        <input
                                                            className='edit_input'
                                                            type="number"
                                                            min={0}
                                                            max={100}
                                                            value={taxPercent}
                                                            onChange={handleTaxChange}
                                                            placeholder='0'
                                                        /> %
                                                    </div>
                                                </div>
                                                <div className='total'>
                                                    <div>Total</div>
                                                    <span>PKR {calculateTotal()}</span>
                                                </div>
                                            </div>
                                            <div className='general_notes'>
                                                <h6>General Notes</h6>
                                                <p>Add any additional notes or observations here.</p>
                                                <textarea
                                                    placeholder='Type your general notes here...'
                                                    rows={7}
                                                    name="note"
                                                    value={values.note}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                {touched.note && errors.note && (
                                                    <div className="error-message" style={{ color: '#ff0000de', fontSize: '12px', marginTop: "3px" }}>
                                                        {errors.note}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default EditServiceEntry;